<template>
  <div>
    <page-header
      :actions="headerActionConfig"
      left-side-class="col-xl-4 pb-2"
      :page="headerConfig"
      right-side-class="col-xl-8 align-items-end d-flex pb-4 justify-content-end"
    />
    <filter-manager
      ref="rubricFilterRef"
      v-model="filter.model"
      :display-advanced="false"
      :filters="filterConfig"
      @search="search()"
    />
    <custom-separator class="pt-3 my-4" text="Resultados" />
    <custom-result-not-found
      v-if="pagination.data.length === 0"
      text="No Hay Información Disponible"
    />
    <div v-for="row in pagination.data" :key="row.id">
      <result-row :data="row" @refresh="search" />
    </div>
    <pagination
      v-if="showPagination"
      :data="pagination"
      show-text
      @pagination-go-page="goToPage"
    />
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import Pagination from 'pagination';
import CustomResultNotFound from 'custom-result-not-found';
import CustomSeparator from 'custom-separator';
import {
  getInitFilters,
  get as getAll,
  getEvaluationTypesByService,
} from '@/modules/rubric/api.js';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import storeMixin from '@/shared/mixins/storeGenerator';
import ResultRow from './components/ResultRow';
import { getServiceTypesByArea } from '@/modules/serviceProposalHistory/api';

const MODULE_NAME = 'RubricSettingModule';

export default {
  name: 'RubricSetting',
  components: {
    PageHeader,
    FilterManager,
    Pagination,
    CustomResultNotFound,
    CustomSeparator,
    ResultRow,
  },
  mixins: [storeMixin, alertMixin],
  data() {
    return {
      moduleName: MODULE_NAME,
      headerConfig: {
        title: 'Configuración de Rúbrica',
        icon: 'fas fa-file-invoice',
        routes: ['Proceso de Competencias', 'Configuración de Rúbrica'],
      },
      validateFilter: false,
      filter: {
        options: {
          academicYears: [],
          areas: [],
          services: [],
          status: [],
          types: [],
        },
        model: {
          academicYear: this.currentAcademicYear(),
          area: null,
          service: null,
          status: null,
          type: null,
        },
      },
      pagination: {
        data: [],
        pageSize: 10,
        currentPage: 1,
        totalItemCount: 0,
        pageCount: 0,
        count: 0,
      },
    };
  },
  computed: {
    headerActionConfig() {
      return [
        {
          text: 'Crear Rúbrica',
          visible: this.canShowAddAdd,
          icon: 'fas fa-plus-circle',
          variant: 'success',
          click: () => this.addRubric(),
        },
      ];
    },
    filterConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          class: 'col-md-4',
          options: this.filter.options.academicYears,
          value: this.filter.model.academicYear,
          initialValue: this.filter.model.academicYear,
          placeholder: 'Año Académico',
          fieldName: 'name',
          fieldKey: 'id',
          validations: this.validateFilter ? 'required' : '',
          error: 'El campo es requerido',
        },
        {
          name: 'area',
          component: 'CustomDropDown',
          placeholder: 'Área',
          value: this.filter.model.area,
          options: this.filter.options.areas,
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-md-4',
          validations: this.validateFilter ? 'required' : '',
          error: 'El campo es requerido',
          onChanged: this.onChangeArea,
        },
        {
          name: 'service',
          component: 'CustomDropDown',
          placeholder: 'Servicio a Ofrecer',
          value: this.filter.model.service,
          options: this.filter.options.services,
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-md-4',
          validations: this.validateFilter ? 'required' : '',
          error: 'El campo es requerido',
          disabled: !this.filter.model.area,
          onChanged: this.onChangeService,
        },
        {
          name: 'type',
          component: 'CustomDropDown',
          placeholder: 'Tipo de Evaluación',
          value: this.filter.model.type,
          options: this.filter.options.types,
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-md-4',
          validations: this.validateFilter ? 'required' : '',
          error: 'El campo es requerido',
          disabled: !this.filter.model.service,
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          placeholder: 'Estatus',
          value: this.filter.model.status,
          options: this.filter.options.status,
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-md-4',
        },
      ];
    },
    canShowAddAdd() {
      return this.userHasPermissions('provider.rubricsettings.create');
    },
    showPagination() {
      return (
        this.pagination.data.length > 0 &&
        this.pagination.totalItemCount > this.pagination.pageSize
      );
    },
  },
  async mounted() {
    await this.fetchInitFilters();
  },
  destroyed() {
    this.persistData();
    sessionStorage.removeItem('rubricAddBoard');
  },
  methods: {
    async fetchInitFilters() {
      const { data } = await getInitFilters();
      this.filter.options = { ...data, services: [], types: [] };
    },
    async search(page = 1) {
      this.pagination.currentPage = page;
      this.validateFilter = false;
      const { pageSize, currentPage: pageIndex } = this.pagination;
      const { data } = await getAll({
        academicYearId: this.filter.model?.academicYear?.id,
        areaId: this.filter.model?.area?.id,
        serviceId: this.filter.model?.service?.id,
        statusId: this.filter?.model?.status?.id,
        typeId: this.filter?.model?.type?.id,
        pageIndex,
        pageSize,
      });
      this.pagination = { pageSize, ...data };
    },
    async goToPage(page) {
      await this.search(page);
    },
    async addRubric() {
      this.validateFilter = true;
      this.$forceUpdate();
      setTimeout(async () => {
        const isValid = await this.$refs.rubricFilterRef.$refs.formValidator.validate();

        if (!isValid) {
          this.ShowToast(
            'Alerta',
            'Favor completar la información solicitada.',
            'error'
          );
          return;
        }

        const { model } = this.filter;
        this.$router.push({
          name: 'RubricAdd',
          params: { model },
        });
      }, 100);
    },
    async onChangeArea(area) {
      this.filter.model.service = null;
      this.filter.model.type = null;
      this.filter.options.services = [];
      this.filter.options.types = [];

      if (!area) return;

      const { data } = await getServiceTypesByArea(area.id);
      this.filter.options.services = data;
    },
    async onChangeService(service) {
      this.filter.model.type = null;
      this.filter.options.types = [];

      if (!service) return;

      const { data } = await getEvaluationTypesByService(service.id);
      this.filter.options.types = data;
    },
  },
  beforeRouteEnter(_, from, next) {
    const persistRoutes = ['RubricAdd', 'RubricEdit', 'RubricPreview'];
    next((vm) => {
      if (persistRoutes.includes(from.name)) vm.search();
    });
  },
};
</script>

<style scoped lang="scss">
@import '@/scss/brand_variables.scss';
</style>
